import { useEffect } from 'react';
import { isSupported, messaging, VAPID_KEY } from '../init-fcm';
import { api } from '../shared/api/api';
import { storage } from '../helpers/storage';

/**
 * Регистрация браузера как устройства для push-уведомлений.
 *
 * Управляющий работает в кабинете, а не в приложении сотрудника, и
 * заказы должен видеть здесь же. Раньше кабинет токен запрашивал, но
 * в чужом проекте Firebase и на сервер его не отправлял — записи об
 * устройстве не возникало, и точечная рассылка его не находила: в
 * журнале доставки стояло «нет устройства».
 *
 * Живёт в `app/`, потому что подключается из `app/App.tsx` — именно
 * он рендерится из `main.tsx`. Прежняя попытка была сделана в старом
 * `src/App.js`, который в сборку не попадает вовсе: он подключён
 * только к неиспользуемому `src/index.js`, и код просто не
 * выполнялся.
 */
export function usePushRegistration() {
  useEffect(() => {
    // Без авторизации регистрировать некого: эндпоинт устройства
    // требует токен.
    const authToken = storage.local.get('token');
    if (!isSupported || !messaging || !authToken) return undefined;

    // Захватываем в локальную переменную: внутри async-функции
    // TypeScript уже не верит, что модульный messaging не null.
    const fcm = messaging;
    let cancelled = false;
    let unsubscribe: (() => void) | null = null;

    const register = async () => {
      try {
        // Спрашиваем разрешение только если его ещё не давали и не
        // отклоняли: повторный запрос браузер всё равно игнорирует.
        if ('Notification' in window && Notification.permission === 'default') {
          await Notification.requestPermission();
        }
        if ('Notification' in window && Notification.permission !== 'granted') {
          return;
        }

        // Service worker обязателен: без него getToken() молча не
        // выдаёт токен. Файл лежит в public/ и отдаётся с корня.
        const registration = await navigator.serviceWorker.register(
          '/firebase-messaging-sw.js',
        );

        const fcmToken = await fcm.getToken({
          vapidKey: VAPID_KEY,
          serviceWorkerRegistration: registration,
        });
        if (!fcmToken || cancelled) return;

        // Тот же эндпоинт, что у мобильного приложения. Платформа
        // 'web' отличает вкладку браузера от телефона — по ней в
        // журнале видно, куда ушло уведомление.
        await api.post('/api/v1/mobile/device', {
          fcm_token: fcmToken,
          platform: 'web',
          // Кабинет и приложение сотрудника — разные домены. Без
          // этого сервер подставлял в уведомление ссылку на
          // приложение горничной, и клик уводил управляющего туда.
          origin: window.location.origin,
        });

        // Пока вкладка кабинета в фокусе, фоновый обработчик в
        // service worker не вызывается вовсе — это правило браузера,
        // а не наша ошибка. Без этого управляющий, который как раз
        // смотрит в кабинет, не видит ничего, хотя доставка в
        // журнале отмечена как «Отправлено».
        unsubscribe = fcm.onMessage((payload: any) => {
          const notification = payload?.notification || {};
          const data = payload?.data || {};
          const url =
            data.type === 'new_concierge_order'
              ? '/concierge/orders'
              : data.type === 'new_task'
              ? '/tasks'
              : '/';

          // Показываем через ту же регистрацию, что и фоновые: тогда
          // клик обрабатывает notificationclick в service worker и
          // открывает нужный раздел в уже открытой вкладке.
          registration.showNotification(notification.title || 'HKeeper', {
            body: notification.body || '',
            icon: '/favicon-32x32.png',
            data: { url },
          });
        });
      } catch {
        // Уведомления — не повод ломать вход в кабинет. Браузер мог
        // запретить их, отказать в service worker или отвалиться по
        // сети; отсутствие устройства видно в журнале доставки.
      }
    };

    register();
    return () => {
      cancelled = true;
      if (unsubscribe) unsubscribe();
    };
  }, []);
}
